import { useState, useEffect, useCallback } from 'react';
import { api } from '@/services/api-class';
import type { FormTabData } from '@/services/api-types';

export interface UseTourStopFormTabsOptions {
  /** The tour stop's id. Pass `null` to skip loading (e.g. before a stop is selected). */
  tourStopId: number | null;
}

export interface UseTourStopFormTabsResult {
  /**
   * One entry per active form instance on the tour stop's client whose schema
   * has at least one `tab: true` field. Each entry carries the schema name as
   * the tab title and the tab-flagged fields as label/displayValue rows.
   * Empty when the stop has no qualifying instances or the principal can't
   * view them.
   */
  tabs: FormTabData[];
  isLoading: boolean;
  error: string | null;
  reload: () => void;
}

/**
 * Loads read-only form tab data for the live tour stop detail page (the extra
 * tabs alongside Tasks / Comments / Attachments). Keyed on the tour stop id —
 * the server resolves the stop's client and projects fields flagged `tab:true`
 * across all live form instances.
 *
 * The endpoint never errors when there's nothing to show; it just returns an
 * empty list. The caller should hide the tab strip entries when `tabs` is
 * empty rather than rendering an empty-state placeholder.
 */
export function useTourStopFormTabs({
  tourStopId,
}: UseTourStopFormTabsOptions): UseTourStopFormTabsResult {
  const [tabs, setTabs] = useState<FormTabData[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    if (tourStopId == null) {
      setTabs([]);
      setError(null);
      return;
    }
    setIsLoading(true);
    setError(null);
    api.getTourStopFormTabs(
      tourStopId,
      (result: FormTabData[] | null | undefined) => {
        setTabs(Array.isArray(result) ? result : []);
        setIsLoading(false);
      },
      (err: any) => {
        setError(err?.message ?? 'Failed to load form tabs for this stop');
        setTabs([]);
        setIsLoading(false);
      },
    );
  }, [tourStopId]);

  useEffect(() => {
    load();
  }, [load]);

  return {
    tabs,
    isLoading,
    error,
    reload: load,
  };
}
